// src/lib/appEvents.js
//
// Tiny typed pub/sub for Casavio. App-wide infrastructure — the tour engine is
// the first consumer, but future consumers (Assist Mode, analytics, work-order
// flows, vendor marketplace) will subscribe here too. No external library — a
// single shared EventTarget under the hood.
//
// WHY THIS EXISTS
// ---------------
// Some features need to react to things that happen deep inside unrelated
// components (the tour's "wow moment" watches the lookup-manual call that lives
// inside AddAppliance). Prop-drilling a callback down through those components
// is brittle and couples them to every surface they touch. Instead, components
// EMIT named events and interested code SUBSCRIBES while it cares. Any signal
// anywhere in the app can reach a listener without either side knowing about
// the other.
//
// EVENT REGISTRY
// --------------
// Every event has: a name, a documented payload shape, the place that emits it,
// and the code expected to subscribe. Keep this table accurate — it is the
// contract.
//
//  ┌────────────────────────┬───────────────────────────────────────────────┐
//  │ manual_lookup_resolved │ Emitted when a lookup-manual invocation         │
//  │                        │ settles (success OR miss OR error).             │
//  │ payload:               │ {                                               │
//  │                        │   status: 'hit' | 'miss' | 'error',             │
//  │                        │   manual_url: string | null,                    │
//  │                        │   make: string | null,                          │
//  │                        │   model: string | null,                         │
//  │                        │   applianceId: string | null                    │
//  │                        │ }                                               │
//  │ emitted by:            │ src/components/AddAppliance.js (lookup-manual    │
//  │                        │ call site, ~line 1757)                          │
//  │ subscribed by:         │ welcome tour step 'wow_moment'                   │
//  ├────────────────────────┼───────────────────────────────────────────────┤
//  │ tour_action            │ A tour bubble CTA asked the host surface to do  │
//  │                        │ something (open a modal, switch a tab, etc).    │
//  │                        │ The tour never reaches into Dashboard state     │
//  │                        │ directly — it asks via this event.              │
//  │ payload:               │ { action: string, payload?: object }            │
//  │ emitted by:            │ src/tour/TourContext.js (dispatchAction)        │
//  │ subscribed by:         │ src/components/Dashboard.js (action router)     │
//  └────────────────────────┴───────────────────────────────────────────────┘
//
// Known actions carried by 'tour_action' (shared vocabulary):
//   - 'open_appliance_add'        → open the Add Appliance flow
//   - 'open_sheet_actions'        → open the Emergency Sheet wizard (print/save/email)
//   - 'focus_emergency'           → switch to the area that holds emergency contacts
//   - 'close_appliance_add'       → close the Add Appliance flow (tour leaving a wait-step)
//   - 'close_emergency_contacts'  → close the Emergency Contacts flow
//   - 'close_sheet_wizard'        → close the Emergency Sheet wizard
//
// Flow-completion SIGNALS — a tour step with `waitsForSignal` advances/cancels
// on these instead of a CTA click; emitted by the real flows on save/close:
//   - 'appliance_saved' / 'appliance_flow_closed'                  (AddAppliance.js)
//   - 'emergency_contact_saved' / 'emergency_contact_flow_closed'  (EmergencyContacts.js)
//   - 'emergency_sheet_generated' / 'emergency_sheet_flow_closed'  (EmergencySheetWizard.js)

export const TOUR_EVENT_TYPES = Object.freeze({
  MANUAL_LOOKUP_RESOLVED: 'manual_lookup_resolved',
  TOUR_ACTION: 'tour_action',
  // Flow-completion signals (see registry above). A tour step's waitsForSignal
  // lists the ones it waits on; the flow components emit them on save/close.
  APPLIANCE_SAVED: 'appliance_saved',
  APPLIANCE_FLOW_CLOSED: 'appliance_flow_closed',
  EMERGENCY_CONTACT_SAVED: 'emergency_contact_saved',
  EMERGENCY_CONTACT_FLOW_CLOSED: 'emergency_contact_flow_closed',
  EMERGENCY_SHEET_GENERATED: 'emergency_sheet_generated',
  EMERGENCY_SHEET_FLOW_CLOSED: 'emergency_sheet_flow_closed',
})

export const TOUR_ACTIONS = Object.freeze({
  OPEN_APPLIANCE_ADD: 'open_appliance_add',
  OPEN_SHEET_ACTIONS: 'open_sheet_actions',
  FOCUS_EMERGENCY: 'focus_emergency',
  // Close actions — dispatched when the tour leaves a step that opened a flow.
  CLOSE_APPLIANCE_ADD: 'close_appliance_add',
  CLOSE_EMERGENCY_CONTACTS: 'close_emergency_contacts',
  CLOSE_SHEET_WIZARD: 'close_sheet_wizard',
})

const VALID_EVENTS = new Set(Object.values(TOUR_EVENT_TYPES))

// One shared bus for the whole app.
const bus = new EventTarget()

// Last payload seen per event type. Lets a listener that mounts a beat AFTER an
// event fired (e.g. the wow_moment step arriving just after lookup-manual
// resolved) still pick it up, instead of waiting forever for an event that
// already happened. Only fire-once-style events benefit; harmless otherwise.
const lastEvents = new Map()

/**
 * Emit an app event.
 * @param {string} type  one of TOUR_EVENT_TYPES
 * @param {object} payload  shape depends on the event (see registry above)
 */
export function emitTourEvent(type, payload = {}) {
  if (!VALID_EVENTS.has(type)) {
    // Loud in dev, harmless in prod — a typo'd event name is a bug.
    console.warn(`[appEvents] unknown event type emitted: "${type}"`)
    return
  }
  lastEvents.set(type, { payload, at: Date.now() })
  bus.dispatchEvent(new CustomEvent(type, { detail: payload }))
}

/**
 * Subscribe to an app event. Returns an unsubscribe function — call it on
 * unmount so listeners stop firing when they leave the screen.
 * @param {string} type  one of TOUR_EVENT_TYPES
 * @param {(payload: object) => void} handler
 * @returns {() => void} unsubscribe
 */
export function onTourEvent(type, handler) {
  if (!VALID_EVENTS.has(type)) {
    console.warn(`[appEvents] unknown event type subscribed: "${type}"`)
    return () => {}
  }
  const wrapped = (e) => handler(e.detail)
  bus.addEventListener(type, wrapped)
  return () => bus.removeEventListener(type, wrapped)
}

/**
 * Read the most recent payload for an event type (or null). Optionally bound
 * how stale it may be via maxAgeMs.
 * @returns {object|null}
 */
export function getLastTourEvent(type, { maxAgeMs = Infinity } = {}) {
  const entry = lastEvents.get(type)
  if (!entry) return null
  if (Date.now() - entry.at > maxAgeMs) return null
  return entry.payload
}

/** Forget a cached event (call after a listener has consumed it). */
export function clearLastTourEvent(type) {
  lastEvents.delete(type)
}
